import { For, Show, createEffect, createSignal, type Component } from 'solid-js';

import { CHAT_CHANNELS, MAX_CHAT_LENGTH, sendChat, type ChatChannel } from '../net/protocol';
import type { GameStore } from '../state/store';

/**
 * The chat box: bottom-left, three channels.
 *
 * ── Why it does not hold the keyboard ───────────────────────────────────────
 *
 * **The input is only focused while you are typing in it**, and Escape gives the keyboard
 * back. That is the whole design constraint: this sits over a game where every letter is a
 * key — `I` is the bag, `Q` is a potion, space is a dodge roll — so an input that took focus
 * on hover, or kept it after sending, would turn the next dodge into the letter *d*.
 *
 * `Enter` opens it and sends; `Escape` closes it. Both are handled here rather than in
 * `game/keyboard.ts`, because the router's job is deciding what a key means *in the world*
 * and while this is focused the answer is "nothing".
 *
 * ── What is missing, and said out loud ──────────────────────────────────────
 *
 * **No history and no moderation.** Messages are delivered to whoever is connected and then
 * gone — nothing is stored server-side, so opening a channel shows an empty log rather than
 * the last hour. There is no mute, no report and no filter. On a server whose accounts are
 * created by hand that is a reasonable place to be; it stops being one the moment
 * registration opens.
 */
export const ChatBox: Component<{ store: GameStore }> = (props) => {
  const [channel, setChannel] = createSignal<ChatChannel>('ZONE');
  const [draft, setDraft] = createSignal('');
  const [typing, setTyping] = createSignal(false);
  let input: HTMLInputElement | undefined;
  let log: HTMLDivElement | undefined;

  /** The lines for the channel being read. */
  const lines = () => props.store.chatLog().filter((m) => m.channel === channel());

  // Pinned to the newest line. Without this the box shows the top of the log and a player
  // watching chat sees nothing arrive.
  createEffect(() => {
    lines();
    if (log) log.scrollTop = log.scrollHeight;
  });

  const submit = (event: Event) => {
    event.preventDefault();
    const text = draft().trim();
    if (text !== '') props.store.send(sendChat(channel(), text));
    setDraft('');
    // **Focus goes back to the game.** Keeping it here would make the next keypress a
    // letter rather than a dodge, which is the thing this box must never do.
    setTyping(false);
    input?.blur();
  };

  return (
    <div class="chat hud-interactive" data-testid="chat-box">
      <div class="chat__tabs" role="tablist" aria-label="Chat channels">
        <For each={CHAT_CHANNELS}>
          {(c) => (
            <button
              type="button"
              role="tab"
              class="chat__tab"
              classList={{ 'chat__tab--on': channel() === c }}
              aria-selected={channel() === c}
              data-testid={`chat-tab-${c}`}
              onClick={() => setChannel(c)}
            >
              {c[0] + c.slice(1).toLowerCase()}
            </button>
          )}
        </For>
      </div>

      <div class="chat__log" ref={log} data-testid="chat-log" aria-live="polite" data-context-menu="allow">
        <Show
          when={lines().length > 0}
          fallback={
            <p class="chat__empty" data-testid="chat-empty">
              Nothing here yet.
            </p>
          }
        >
          <For each={lines()}>
            {(msg) => (
              <p class="chat__line">
                <span class="chat__from">{msg.fromName}</span>
                <span class="chat__text">{msg.text}</span>
              </p>
            )}
          </For>
        </Show>
      </div>

      <form class="chat__form" onSubmit={submit}>
        <input
          ref={input}
          type="text"
          class="chat__input"
          data-testid="chat-input"
          // The cap is the server's, and it enforces it regardless — this only stops the
          // player typing a sentence that will be silently cut.
          maxLength={MAX_CHAT_LENGTH}
          placeholder={`Say something in ${channel().toLowerCase()}…`}
          aria-label={`Message ${channel().toLowerCase()}`}
          value={draft()}
          onFocus={() => setTyping(true)}
          onBlur={() => setTyping(false)}
          onInput={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            // Escape gives the keyboard back to the game without sending. Stopped here so
            // the panel router does not also read it as "close whatever is open".
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              setDraft('');
              input?.blur();
              return;
            }
            // **No `stopPropagation` here, deliberately.** Solid delegates `onKeyDown` to
            // the document, so stopping it happens *at* the document and cannot beat a
            // listener already attached there — it would look like a protection and be
            // none. The real guard is `isTypingTarget` in `game/keyboard.ts`, which
            // refuses every key whose target is an `<input>`; `keyboard.test.ts` holds it.
            //
            // Escape is handled above because that one is *local* behaviour — giving the
            // keyboard back — not a thing the router should do instead.
          }}
        />
      </form>

      {/* Only while focused, so it is a reminder at the moment it applies rather than
          permanent chrome. */}
      <Show when={typing()}>
        <p class="chat__hint" data-testid="chat-hint">
          Esc to return to the game
        </p>
      </Show>
    </div>
  );
};
